import { Formik } from 'formik';
import { Plug2 } from 'lucide-react';

import { KubesoloEnvironmentSettings, KubesoloTest } from '@api/types.gen';

import {
  ContainerEngine,
  EnvironmentId,
} from '@/react/portainer/environments/types';
import { Credential } from '@/react/portainer/settings/sharedCredentials/types';
import { useSettings } from '@/react/portainer/settings/queries';
import { Settings } from '@/react/portainer/settings/types';
import { UNASSIGNED_GROUP_ID } from '@/react/portainer/environments/environment-groups/utils/getPlatformLabel';

import { LoadingButton } from '@@/buttons/LoadingButton';
import { FormSection } from '@@/form-components/FormSection';
import { Link } from '@@/Link';
import { TextTip } from '@@/Tip/TextTip';
import { confirm } from '@@/modals/confirm';
import { ModalType } from '@@/modals';
import { buildConfirmButton } from '@@/modals/utils';

import { MoreSettingsSection } from '../shared/MoreSettingsSection';
import { EdgeAgentFieldset } from '../shared/EdgeAgentTab/EdgeAgentForm/EdgeAgentFieldset';
import { CheckinIntervalsSection } from '../shared/EdgeAgentTab/EdgeAgentForm/CheckinIntervalsSection';
import { buildInitialValues as buildEdgeAgentValues } from '../shared/EdgeAgentTab/EdgeAgentForm/EdgeAgentForm';

import { ClearTestButton } from './ConnectionTestResults/ClearTestButton';
import { ConnectionTestResults } from './ConnectionTestResults/ConnectionTestResults';
import { InstallButton } from './ConnectionTestResults/InstallButton';
import { KubesoloVersionField } from './KubesoloVersionField';
import { MachinesField } from './MachinesField';
import { OfflineBundlesField } from './OfflineBundlesField';
import { useKubesoloVersions } from './queries/useKubesoloVersions';
import { versionToInstall } from './versionToInstall';
import { SSHCredentialField } from './SSHCredentialField';
import { formatMachines, parseMachines } from './parseMachines';
import { validationSchema } from './SSHInstallForm.validation';
import { isInProgress } from './queries/useActiveConnectionTest';
import { useClearConnectionTestMutation } from './queries/useClearConnectionTestMutation';
import { useStartConnectionTestMutation } from './queries/useStartConnectionTestMutation';
import { FormValues } from './types';

interface Props {
  asyncMode: boolean;
  credentials: Array<Credential>;
  activeTest?: KubesoloTest | null;
  onInstalled(environmentIds: Array<EnvironmentId>): void;
}

export function SSHInstallForm({
  asyncMode,
  credentials,
  activeTest,
  onInstalled,
}: Props) {
  const settingsQuery = useSettings();
  const versionsQuery = useKubesoloVersions();
  const startMutation = useStartConnectionTestMutation();
  const clearMutation = useClearConnectionTestMutation();

  if (!settingsQuery.data) {
    return null;
  }

  return (
    <Formik<FormValues>
      initialValues={buildInitialValues(
        settingsQuery.data,
        credentials,
        activeTest
      )}
      onSubmit={handleSubmit}
      validateOnMount
      validationSchema={validationSchema(asyncMode)}
    >
      {({ values, isValid, submitForm }) => (
        // not a <form>: React bubbles the credential drawer's submit through its portal
        <div aria-label="Install over SSH" role="group">
          {!!settingsQuery.data.Edge?.MTLS?.UseSeparateCert && (
            <TextTip color="orange" className="mb-2" inline={false}>
              Edge mTLS is enabled in{' '}
              <Link
                to="portainer.settings.edgeCompute"
                data-cy="kubesolo-mtls-settings-link"
              >
                Edge Compute settings
              </Link>
              . KubeSolo installs its Edge Agent without a client certificate,
              so the installed machines won&apos;t be able to connect to
              Portainer.
            </TextTip>
          )}

          <SSHCredentialField credentials={credentials} />

          {!!values.credentialId && (
            <>
              <FormSection title="Portainer server details">
                <EdgeAgentFieldset
                  showName={false}
                  asyncMode={asyncMode}
                  containerEngine={ContainerEngine.Kubernetes}
                />
              </FormSection>

              <FormSection title="KubeSolo">
                <KubesoloVersionField offline={values.offline} />
                <OfflineBundlesField />
              </FormSection>

              <FormSection title="Machines">
                <MachinesField />
              </FormSection>

              <div className="flex items-center gap-2">
                <LoadingButton
                  type="button"
                  className="vertical-center"
                  icon={Plug2}
                  isLoading={clearMutation.isLoading || startMutation.isLoading}
                  loadingText="Starting the test..."
                  disabled={!isValid}
                  onClick={submitForm}
                  data-cy="kubesolo-test-connections-button"
                >
                  Test connections
                </LoadingButton>
                {activeTest && <ClearTestButton test={activeTest} />}
              </div>

              {activeTest && <ConnectionTestResults test={activeTest} />}

              <MoreSettingsSection showGroupInheritance>
                <CheckinIntervalsSection asyncMode={asyncMode} />
              </MoreSettingsSection>

              {activeTest && (
                <InstallButton
                  test={activeTest}
                  environmentSettings={buildEnvironmentSettings(
                    values,
                    asyncMode
                  )}
                  changedSinceTest={changedSinceTest(
                    values,
                    activeTest,
                    asyncMode,
                    settingsQuery.data,
                    versionsQuery.data?.bundled
                  )}
                  onInstalled={onInstalled}
                />
              )}
            </>
          )}
        </div>
      )}
    </Formik>
  );

  async function handleSubmit({ credentialId, ...values }: FormValues) {
    if (!credentialId) {
      return;
    }

    if (!activeTest) {
      start(credentialId);
      return;
    }

    if (await confirmReplacing(activeTest)) {
      clearMutation.mutate(activeTest.id, {
        onSuccess: () => start(credentialId),
      });
    }

    function start(credentialID: Credential['id']) {
      startMutation.mutate({
        credentialID,
        portainerURL: values.portainerUrl,
        tunnelServerAddress: asyncMode ? '' : values.tunnelServerAddr,
        hosts: parseMachines(values.machines).hosts,
        // an empty version leaves the server to pick the bundled release, the only one offline machines can install
        kubesoloVersion: values.offline ? '' : values.kubesoloVersion,
        offline: values.offline,
        environmentSettings: buildEnvironmentSettings(values, asyncMode),
      });
    }
  }
}

function buildEnvironmentSettings(
  values: FormValues,
  asyncMode: boolean
): KubesoloEnvironmentSettings {
  return {
    asyncMode,
    pollFrequency: values.pollFrequency,
    pingInterval: values.edge.PingInterval,
    snapshotInterval: values.edge.SnapshotInterval,
    commandInterval: values.edge.CommandInterval,
    groupID: values.meta.groupId ?? UNASSIGNED_GROUP_ID,
    tagIDs: values.meta.tagIds ?? [],
  };
}

/** The fields the test checked from the machines, which an install can't change without testing again. */
function changedSinceTest(
  values: FormValues,
  test: KubesoloTest,
  asyncMode: boolean,
  settings: Settings,
  bundledVersion?: string
) {
  // a test without a tunnel address used the default, as the form shows
  const testedTunnelAddr =
    test.tunnelServerAddress || buildEdgeAgentValues(settings).tunnelServerAddr;
  const version = versionToInstall(values, bundledVersion);

  const credentialChanged = values.credentialId !== test.credentialID;
  const portainerUrlChanged = values.portainerUrl !== test.portainerURL;
  // Async agents don't use the tunnel
  const tunnelAddrChanged =
    !asyncMode && values.tunnelServerAddr !== testedTunnelAddr;
  // the test records the bundled release by name, which is unknown until the versions load
  const versionChanged =
    version !== undefined && version !== test.kubesoloVersion;
  const internetAccessChanged = values.offline !== test.offline;

  const fields: Array<[changed: boolean, label: string]> = [
    [credentialChanged, 'SSH credential'],
    [portainerUrlChanged, 'Portainer API server URL'],
    [tunnelAddrChanged, 'Portainer tunnel server address'],
    [versionChanged, 'KubeSolo version'],
    [internetAccessChanged, 'internet access'],
  ];

  return fields.filter(([changed]) => changed).map(([, label]) => label);
}

/** Starts from the test Portainer holds, so coming back to the wizard picks up where it was left. */
function buildInitialValues(
  settings: Settings,
  credentials: Array<Credential>,
  test?: KubesoloTest | null
): FormValues {
  const defaults = buildEdgeAgentValues(settings);
  const testCredential = credentials.find(
    (credential) => credential.id === test?.credentialID
  );
  const credentialId = (testCredential ?? credentials[0])?.id;

  if (!test) {
    return {
      ...defaults,
      credentialId,
      machines: '',
      kubesoloVersion: '',
      offline: false,
    };
  }

  const { environmentSettings } = test;
  return {
    credentialId,
    machines: formatMachines(test.hosts),
    kubesoloVersion: test.kubesoloVersion,
    offline: test.offline,
    portainerUrl: test.portainerURL,
    tunnelServerAddr: test.tunnelServerAddress || defaults.tunnelServerAddr,
    pollFrequency: environmentSettings.pollFrequency,
    meta: {
      ...defaults.meta,
      groupId: environmentSettings.groupID,
      tagIds: environmentSettings.tagIDs,
    },
    edge: {
      PingInterval: environmentSettings.pingInterval,
      SnapshotInterval: environmentSettings.snapshotInterval,
      CommandInterval: environmentSettings.commandInterval,
    },
  };
}

function confirmReplacing(test: KubesoloTest) {
  return confirm({
    title: 'Start a new connection test?',
    modalType: ModalType.Warn,
    message: isInProgress(test)
      ? 'The running test will be cancelled and its results cleared.'
      : 'The current results will be cleared.',
    confirmButton: buildConfirmButton('Start new test', 'warning'),
  });
}
